import {NgModule} from '@angular/core';
import {RouterModule, Routes} from '@angular/router';
import {NotFoundErrorComponent} from "./components/not-found-error/not-found-error.component";
import {InternalServerErrorComponent} from "./components/internal-server-error/internal-server-error.component";
import {UnauthorizedErrorComponent} from "./components/unauthorized-error/unauthorized-error.component";
import {ForbiddenErrorComponent} from "./components/forbidden-error/forbidden-error.component";

const routes: Routes = [
    {
        path: '',
        redirectTo: '/dashboard',
        pathMatch: 'full'
    },
    {
        path: 'auth',
        loadChildren: () =>
            import('./modules/auth/auth.module').then((m) => m.AuthModule),
    },
    {
        path: 'admin',
        loadChildren: () =>
            import('./modules/admin/admin.module').then((m) => m.AdminModule),
    },
    {
        path: 'dashboard',
        loadChildren: () =>
            import('./modules/dashboard/dashboard.module').then((m) => m.DashboardModule),
    },
    {
        path: 'games',
        loadChildren: () =>
            import('./modules/games/games.module').then((m) => m.GamesModule),
    },
    {
        path: 'operators',
        title: 'Partners',
        loadChildren: () =>
            import('./modules/operators/operators/operators.module').then((m) => m.OperatorsModule),
    },
    {
        path: 'operator_brands',
        title: 'Brands',
        loadChildren: () =>
            import('./modules/operators/operator-brands/operator-brands.module').then((m) => m.OperatorBrandsModule),
    },
    {
        path: 'operator_affiliate_systems',
        title: 'Affiliate Platforms',
        loadChildren: () =>
            import('./modules/operators/operator-affiliate-systems/operator-affiliate-systems.module').then((m) => m.OperatorAffiliateSystemsModule),
    },
    {
        path: 'operator_logins',
        title: 'Accounts',
        loadChildren: () =>
            import('./modules/operators/operator-logins/operator-logins.module').then((m) => m.OperatorLoginsModule),
    },
    {
        path: 'authoritys',
        title: 'Authorities',
        loadChildren: () =>
            import('./modules/authorities/authorities.module').then((m) => m.AuthoritiesModule),
    },
    {
        path: 'operator_licences',
        title: 'Operator Licences',
        loadChildren: () =>
            import('./modules/operators/operator-licences/operator-licences.module').then((m) => m.OperatorLicencesModule),
    },
    {
        path: 'sales',
        loadChildren: () =>
            import('./modules/sales/sales.module').then((m) => m.SalesModule),
    },
    {
        path: 'users',
        canActivate: [],
        loadChildren: () =>
            import('./modules/users/users.module').then((m) => m.UsersModule),
    },
    {
        path: 'persons',
        title: 'People',
        canActivate: [],
        loadChildren: () =>
            import('./modules/persons/persons.module').then((m) => m.PersonsModule),
    },
    {
        path: 'freelancers',
        canActivate: [],
        loadChildren: () =>
            import('./modules/freelancers/freelancers.module').then((m) => m.FreelancersModule),
    },
    {
        path: 'groups',
        canActivate: [],
        loadChildren: () =>
            import('./modules/groups/groups.module').then((m) => m.GroupsModule),
    },
    {
        path: 'payment_providers',
        loadChildren: () =>
            import('./modules/payment-providers/payment-providers.module').then((m) => m.PaymentProvidersModule),
    },
    {
        path: 'payment_accounts',
        loadChildren: () =>
            import('./modules/payment-accounts/payment-accounts.module').then((m) => m.PaymentAccountsModule),
    },
    {
        path: 'company_units',
        title: 'Company Units',
        loadChildren: () =>
            import('./modules/company-units/company-units.module').then((m) => m.CompanyUnitsModule),
    },
    {
        path: 'website_pages',
        title: 'Website Pages',
        loadChildren: () =>
            import('./modules/website-pages/website-pages.module').then((m) => m.WebsitePagesModule),
    },
    {
        path: 'affiliate_systems',
        loadChildren: () =>
            import('./modules/affiliate-systems/affiliate-systems.module').then((m) => m.AffiliateSystemsModule),
    },
    {
        path: 'websites',
        loadChildren: () =>
            import('./modules/websites/websites.module').then((m) => m.WebsitesModule),
    },
    {
        path: 'content-loop',
        title: 'Content Loop',
        loadChildren: () =>
            import('./modules/content-loop/content-loop.module').then((m) => m.ContentLoopModule),
    },
    {
        // The content managers' simple mode of the SEO loop (docs/plans/seo-loop-simple-mode/PLAN.md).
        // layout 'bare': its own header, no Hexagon navbar or breadcrumb (plan §7, option A; services/layout.service.ts).
        path: 'loop',
        title: 'Weekly loop',
        data: {layout: 'bare'},
        loadChildren: () =>
            import('./modules/loop-simple/loop-simple.module').then((m) => m.LoopSimpleModule),
    },
    {
        path: 'domains',
        loadChildren: () =>
            import('./modules/domains/domains.module').then((m) => m.DomainsModule),
    },
    {
        path: 'website_scripts',
        title: 'Website Scripts',
        loadChildren: () =>
            import('./modules/website-scripts/website-scripts.module').then((m) => m.WebsiteScriptsModule),
    },
    {
        path: 'page_templates',
        title: 'Page Templates',
        loadChildren: () =>
            import('./modules/page-templates/page-templates.module').then((m) => m.PageTemplatesModule),
    },
    {
        path: 'languages',
        loadChildren: () =>
            import('./modules/languages/languages.module').then((m) => m.LanguagesModule),
    },
    {
        path: 'countries',
        loadChildren: () =>
            import('./modules/countries/countries.module').then((m) => m.CountriesModule),
    },
    {
        path: 'currencies',
        loadChildren: () =>
            import('./modules/currencies/currencies.module').then((m) => m.CurrenciesModule),
    },
    {
        path: 'currency-management',
        title: 'Currency Management',
        loadChildren: () =>
            import('./modules/finance/currency-management/currency-management.module').then((m) => m.CurrencyManagementModule),
    },
    {
        path: 'finance_periods',
        loadChildren: () =>
            import('./modules/finance/finance-periods/finance-periods.module').then((m) => m.FinancePeriodsModule),
    },
    {
        path: 'collections',
        loadChildren: () =>
            import('./modules/collections/collections.module').then((m) => m.CollectionsModule),
    },
    {
        path: 'subaffiliation',
        loadChildren: () =>
            import('./modules/subaffiliation/subaffiliation.module').then((m) => m.SubAffiliationModule),
    },
    {
        path: 'offers',
        loadChildren: () =>
            import('./modules/offers/offers.module').then((m) => m.OffersModule),
    },
    {
        path: 'legal_companies',
        canActivate: [],
        loadChildren: () =>
            import('./modules/legal-companies/legal-companies.module').then((m) => m.LegalCompaniesModule),
    },
    {
        path: 'dmca-complaints',
        title: 'DMCA Complaints',
        loadChildren: () =>
            import('./modules/dmca-complaints/dmca-complaints.module').then((m) => m.DmcaComplaintsModule),
    },
    {
        path: 'google-takedowns',
        title: 'Google Takedowns',
        loadChildren: () =>
            import('./modules/google-takedowns/google-takedowns.module').then((m) => m.GoogleTakedownsModule),
    },
    {
        path: 'metronic',
        canActivate: [],
        loadChildren: () =>
            import('./_metronic/layout/layout.module').then((m) => m.LayoutModule),
    },
    { path: 'error_401', component: UnauthorizedErrorComponent },
    { path: 'error_403', component: ForbiddenErrorComponent },
    { path: 'error_404', component: NotFoundErrorComponent },
    { path: 'error_500', component: InternalServerErrorComponent },
    { path: '**', pathMatch: 'full', component: NotFoundErrorComponent },
];

@NgModule({
    imports: [RouterModule.forRoot(routes)],
    exports: [RouterModule]
})
export class AppRoutingModule {
}
